import React from "react";
import { useI18n } from "@/context/I18nContext";

export const StatusBadge = ({ status }) => {
  const { tStatus } = useI18n();
  const map = {
    aktiv: "lime", aktiv_: "lime", online: "lime", bezahlt: "lime", gewonnen: "lime",
    paid: "lime", neu: "amber", offen: "amber", wartung: "amber", in_bearbeitung: "amber",
    in_progress: "amber", review: "amber", todo: "slate", offline: "red", überfällig: "red",
    verloren: "red", done: "lime", maintenance: "amber",
    in_vorbereitung: "amber", pausiert: "amber", gekündigt: "red",
    entwurf: "slate", bereit: "amber", gesendet: "lime", angebot_vorbereitet: "lime",
    archiviert: "slate", nicht_verbunden: "slate", bereit_fuer_bexio: "amber",
    in_arbeit: "amber", warten_auf_kunde: "amber", pruefung: "amber", erledigt: "lime",
  };
  const tone = map[status] || "slate";
  const cls = {
    lime: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30",
    amber: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    slate: "bg-white/10 text-gray-300 border-white/10",
  }[tone];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${cls}`}>
      {tStatus(status)}
    </span>
  );
};

export const StatCard = ({ label, value, icon: Icon, accent, sub, testid }) => (
  <div data-testid={testid} className="bg-[#121217] border border-white/10 rounded-xl p-5 sm:p-6 hover:border-white/20 transition-colors group">
    <div className="flex items-start justify-between">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{label}</span>
      {Icon && <Icon className={`w-5 h-5 ${accent ? "text-[#A3E635]" : "text-gray-500"} group-hover:scale-110 transition-transform`} />}
    </div>
    <div className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter text-white">{value}</div>
    {sub && <div className="mt-1 text-sm text-gray-500">{sub}</div>}
  </div>
);

export const Card = ({ children, className = "", ...props }) => (
  <div className={`bg-[#121217] border border-white/10 rounded-xl ${className}`} {...props}>{children}</div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">{title}</h1>
      {subtitle && <p className="mt-1 text-gray-400">{subtitle}</p>}
    </div>
    {children && <div className="flex gap-3">{children}</div>}
  </div>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-8 h-8 border-2 border-white/10 border-t-[#A3E635] rounded-full animate-spin" />
  </div>
);

export const Empty = ({ text, hint }) => (
  <div className="text-center py-16 text-sm">
    <div className="text-gray-400">{text}</div>
    {hint && <p className="text-xs text-gray-500 mt-1.5 max-w-[340px] mx-auto leading-relaxed">{hint}</p>}
  </div>
);

export const Avatar = ({ name, color = "#A3E635", size = 40, src }) => {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const resolved = src && !src.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
  if (resolved) return <img src={resolved} alt={name} style={{ width: size, height: size }} className="rounded-lg object-cover border border-white/10" />;
  return (
    <div style={{ width: size, height: size, background: `${color}22`, color }}
      className="rounded-lg flex items-center justify-center font-bold text-sm border border-white/10 shrink-0">
      {initials}
    </div>
  );
};

export function chf(v) {
  return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 0 }).format(v || 0);
}

// Schweizer Format: ganze Beträge als "CHF 390.–", mit Rappen "CHF 360.78"
export function chfFr(v) {
  const n = Number(v || 0);
  const neg = n < 0;
  const abs = Math.abs(n);
  const hasRappen = Math.abs(abs - Math.round(abs)) > 0.005;
  const fixed = abs.toFixed(2);
  const [intRaw, dec] = fixed.split(".");
  const intPart = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${neg ? "-" : ""}CHF ${intPart}.${hasRappen ? dec : "–"}`;
}
